/**
 * Overlap semantics. These functions ARE the contract for "do two scopes collide":
 * the mock coordinator, the real coordinator (WP-02) and the planner's pre-check (WP-06)
 * all use them, so every part of Botallion agrees on what an overlap is.
 *
 * Rules:
 * - paths: two globs overlap when at least one file path could match both (glob intersection).
 * - symbols: exact string match.
 * - contracts: exact string match.
 * - migration: two scopes that both set the migration flag always overlap.
 *
 * Severity: migration overlaps always `block`. Any other overlap blocks when either claim is
 * `exclusive`, and only warns when both are `shared`.
 */
import type { ClaimMode } from "./claim.ts";
import type { OverlapKind, OverlapSeverity } from "./overlap.ts";
import type { Scope } from "./primitives.ts";

type Token = { t: "lit"; ch: string } | { t: "one" } | { t: "star" };
type Segment = { globstar: true } | { globstar: false; tokens: Token[] };

const MAX_BRACE_EXPANSIONS = 256;

/** Expand `{a,b}` alternatives (nesting allowed) into plain globs. */
export function expandBraces(glob: string): string[] {
  const open = glob.indexOf("{");
  if (open === -1) return [glob];
  let depth = 0;
  let close = -1;
  const commas: number[] = [];
  for (let i = open; i < glob.length; i++) {
    const c = glob[i];
    if (c === "{") depth++;
    else if (c === "}") {
      depth--;
      if (depth === 0) {
        close = i;
        break;
      }
    } else if (c === "," && depth === 1) commas.push(i);
  }
  // Unbalanced or no alternatives: treat the brace literally.
  if (close === -1 || commas.length === 0) {
    const head = glob.slice(0, open + 1);
    return expandBraces(glob.slice(open + 1)).map((rest) => head + rest);
  }
  const prefix = glob.slice(0, open);
  const suffix = glob.slice(close + 1);
  const bounds = [open, ...commas, close];
  const out: string[] = [];
  for (let k = 0; k < bounds.length - 1; k++) {
    const alt = glob.slice(bounds[k]! + 1, bounds[k + 1]);
    for (const expanded of expandBraces(prefix + alt + suffix)) {
      out.push(expanded);
      if (out.length > MAX_BRACE_EXPANSIONS) throw new Error(`glob expands to more than ${MAX_BRACE_EXPANSIONS} alternatives: ${glob}`);
    }
  }
  return out;
}

function normalise(glob: string): string {
  let g = glob.trim().replace(/\/{2,}/g, "/");
  if (g.endsWith("/")) g += "**";
  return g;
}

function parseSegment(seg: string, literal: boolean): Segment {
  if (!literal && seg === "**") return { globstar: true };
  const tokens: Token[] = [];
  for (const ch of seg) {
    if (!literal && ch === "*") {
      // Collapse runs of `*` (so `a**b` behaves like `a*b`).
      if (tokens.at(-1)?.t !== "star") tokens.push({ t: "star" });
    } else if (!literal && ch === "?") tokens.push({ t: "one" });
    else tokens.push({ t: "lit", ch });
  }
  return { globstar: false, tokens };
}

function parse(glob: string, literal: boolean): Segment[] {
  return normalise(glob)
    .split("/")
    .map((seg) => parseSegment(seg, literal));
}

/**
 * Generic "is the intersection of two wildcard languages non-empty" search.
 * `isStar` marks elements that match zero or more of the other side's elements;
 * `compatible` decides whether two non-star elements can match the same thing.
 */
function intersects<T>(a: T[], b: T[], isStar: (x: T) => boolean, compatible: (x: T, y: T) => boolean): boolean {
  const seen = new Set<number>();
  const width = b.length + 1;
  const visit = (i: number, j: number): boolean => {
    if (i === a.length && j === b.length) return true;
    const key = i * width + j;
    if (seen.has(key)) return false;
    seen.add(key);
    const x = a[i];
    const y = b[j];
    if (x !== undefined && isStar(x)) {
      if (visit(i + 1, j)) return true;
      if (y !== undefined && !isStar(y) && visit(i, j + 1)) return true;
    }
    if (y !== undefined && isStar(y)) {
      if (visit(i, j + 1)) return true;
      if (x !== undefined && !isStar(x) && visit(i + 1, j)) return true;
    }
    if (x !== undefined && y !== undefined && !isStar(x) && !isStar(y) && compatible(x, y)) return visit(i + 1, j + 1);
    return false;
  };
  return visit(0, 0);
}

function tokensCompatible(x: Token, y: Token): boolean {
  if (x.t === "lit" && y.t === "lit") return x.ch === y.ch;
  return true;
}

function segmentsIntersect(a: Segment[], b: Segment[]): boolean {
  return intersects(
    a,
    b,
    (s) => s.globstar,
    (x, y) => !x.globstar && !y.globstar && intersects(x.tokens, y.tokens, (t) => t.t === "star", tokensCompatible),
  );
}

/**
 * True when at least one file path could match both globs.
 * Supports `*`, `**`, `?` and `{a,b}`; a trailing `/` means "everything under this directory".
 *
 * @example globsOverlap("demo/api/src/**", "demo/api/src/routes/*.ts") // true
 * @example globsOverlap("demo/api/src/*.ts", "demo/web/src/*.ts") // false
 */
export function globsOverlap(a: string, b: string): boolean {
  for (const x of expandBraces(a)) {
    for (const y of expandBraces(b)) {
      if (segmentsIntersect(parse(x, false), parse(y, false))) return true;
    }
  }
  return false;
}

/**
 * True when a concrete repo-relative file path matches a glob.
 *
 * @example pathMatchesGlob("demo/api/migrations/0003_seasons.sql", "demo/api/migrations/**") // true
 */
export function pathMatchesGlob(path: string, glob: string): boolean {
  const literal = parse(path, true);
  return expandBraces(glob).some((g) => segmentsIntersect(literal, parse(g, false)));
}

/** One kind of collision between two scopes, as found by {@link detectScopeOverlaps}. */
export interface ScopeOverlap {
  /** What collided. Never `diff`: that kind only comes from comparing pushed diffs. */
  kind: Exclude<OverlapKind, "diff">;
  /** Block or warn under the severity rules. */
  severity: OverlapSeverity;
  /** The concrete things that collided. For paths, `a` when both globs are equal, otherwise `a <> b`. */
  subjects: string[];
}

/** The severity rule. Migration overlaps always block; otherwise block unless both claims are shared. */
export function overlapSeverity(kind: OverlapKind, modeA: ClaimMode, modeB: ClaimMode): OverlapSeverity {
  if (kind === "migration") return "block";
  return modeA === "shared" && modeB === "shared" ? "warn" : "block";
}

/**
 * Compare two scopes and return at most one {@link ScopeOverlap} per kind (path, symbol, contract,
 * migration), in that order. An empty array means the scopes do not collide.
 * Pass the two claims' modes to get the right severity (both default to `exclusive`).
 */
export function detectScopeOverlaps(
  a: Scope,
  b: Scope,
  modeA: ClaimMode = "exclusive",
  modeB: ClaimMode = "exclusive",
): ScopeOverlap[] {
  const out: ScopeOverlap[] = [];
  const push = (kind: ScopeOverlap["kind"], subjects: string[]) => {
    if (subjects.length > 0) out.push({ kind, severity: overlapSeverity(kind, modeA, modeB), subjects: [...new Set(subjects)] });
  };

  const paths: string[] = [];
  for (const pa of a.paths) {
    for (const pb of b.paths) {
      if (globsOverlap(pa, pb)) paths.push(pa === pb ? pa : `${pa} <> ${pb}`);
    }
  }
  push("path", paths);
  push("symbol", a.symbols.filter((s) => b.symbols.includes(s)));
  push("contract", a.contracts.filter((c) => b.contracts.includes(c)));
  push("migration", a.migration && b.migration ? ["migration"] : []);
  return out;
}

/** True when a scope covers a concrete file path (any of its globs matches). */
export function scopeCoversPath(scope: Scope, path: string): boolean {
  return scope.paths.some((g) => pathMatchesGlob(path, g));
}
